import * as React from 'react'
import * as ReactRedux from 'react-redux'
import PropTypes from 'prop-types'

import * as Herz from '@rushplay/herz'
import * as Notifications from '@rushplay/notifications'

import * as Promotions from '../promotions'
import * as Configuration from '../configuration'
import * as Player from '../player'
import * as ServerConfiguration from '../server-configuration'

/**
 * Builds a bonus object for the FluidProvider.
 * This function extracts the necessary information from the offer
 * and formats it according to the requirements of the FluidProvider.
 *
 * @param {object} offer
 * @param {function} translate
 *
 * @example
 * const bonus = buildBonus(offer, translate)
 *  @returns
 * * @typedef {Object} Bonus
 * * @property {string} code - Unique identifier for the bonus.
 * * @property {string} title - Title of the bonus
 * * @property {string} description - Description of the bonus
 * * @property {string} logoUrl - URL of the bonus logo
 * * @property {string} maxBonus - Maximum bonus amount in string format.
 * * @property {string} maxBonusPercentage - Maximum bonus percentage in string format.
 * * @property {string} minDeposit - Minimum deposit required for the bonus in string format.
 * * @property {boolean} selected - Indicates if the bonus is selected.
 * * @property {string} termsAndConditions - Terms and conditions for the bonus, formatted as HTML.
 * * @returns {Bonus} The formatted bonus object.
 */
export function buildBonus(offer, translate) {
  const id = offer?.key.toString() || 'default'

  const rewards = offer?.campaign.rewards || []

  const promotionMinMax = Promotions.getPromotionMinMax(rewards)

  return {
    code: id,
    title: translate(`promotions.${id}.title`)
      ? translate(`promotions.${id}.title`)
      : `promotions.${id}.title`,
    description: translate(`promotions.${id}.description`)
      ? translate(`promotions.${id}.description`)
      : `promotions.${id}.description`,
    logoUrl: translate(`promotions.${id}.img-src`)
      ? translate(`promotions.${id}.img-src`)
      : `promotions.${id}.img-src`,
    maxBonus: (promotionMinMax?.maxBonusPercentItem / 100).toString(),
    maxBonusPercentage: 0,
    minDeposit: (promotionMinMax?.minDepositForAnyReward / 100).toString(),
    selected:
      offer?.campaign?.optInState === 'in' ||
      offer?.campaign?.optInState === 'automatic',
    termsAndConditions: translate(`promotions.${id}.terms-conditions`) || '',
  }
}

/**
 * Builds a bonus object for the FluidProvider.
 * This function extracts the necessary information from the offer
 * and formats it according to the requirements of the FluidProvider.
 *
 * @param {object} offer
 * @param {function} translate
 *
 * @example
 * const depositOffers = buildDepositOffers(offers, translate)
 *  @returns
 * * @typedef {Object} Bonus
 * * @property {string} code - Unique identifier for the bonus.
 * * @property {string} title - Title of the bonus
 * * @property {string} description - Description of the bonus
 * * @property {string} logoUrl - URL of the bonus logo
 * * @property {string} maxBonus - Maximum bonus amount in string format.
 * * @property {string} maxBonusPercentage - Maximum bonus percentage in string format.
 * * @property {string} minDeposit - Minimum deposit required for the bonus in string format.
 * * @property {boolean} selected - Indicates if the bonus is selected.
 * * @property {string} termsAndConditions - Terms and conditions for the bonus, formatted as HTML.
 * * @returns {Bonus} The formatted bonus object.
 */
export function buildDepositOffer(offer, translate) {
  const id = offer?.id.toString() || 'default'

  return {
    code: id,
    title: translate(`deposit-offer.${id}.title`)
      ? translate(`deposit-offer.${id}.title`)
      : `deposit-offer.${id}.title`,
    description: translate(`deposit-offer.${id}.description`)
      ? translate(`deposit-offer.${id}.description`)
      : `deposit-offer.${id}.description`,
    logoUrl: translate(`deposit-offer.${id}.img-src`)
      ? translate(`deposit-offer.${id}.img-src`)
      : `deposit-offer.${id}.img-src`,
    maxBonus: (offer?.bonusMoneyPercentMaxCents / 100).toString(),
    maxBonusPercentage: offer?.bonusMoneyPercent,
    minDeposit:
      offer?.rangeLimits[0] === 0
        ? '0'
        : (offer?.rangeLimits[0] / 100).toString(),
    selected: true,
    termsAndConditions: translate(`deposit-offer.${id}.terms-conditions`) || '',
  }
}
/**
 * @typedef {Object} FluidUserData
 * @property {number|null} age
 * @property {string} fullName
 * @property {string|null} affiliateId
 * @property {number|null} depositCount
 * @property {number|null} previousDeposit
 * @property {number|null} lifetimeDeposit
 */

/**
 * @typedef {Object} FluidParams
 * @property {string} userId
 * @property {string} sessionId
 * @property {string} locale
 * @property {string} countryCode
 * @property {string} currencyCode
 * @property {FluidUserData} userData
 * @property {Array} bonuses
 * @property {string} widgetUrl
 * @property {string} operatorId
 */

/**
 * Calculates the player's age based on their birthdate.
 *
 * @param {string|null} birthDate - The player's birthdate in ISO format (YYYY-MM-DD).
 * @returns {number|null} The age in years or null if invalid.
 */
export function getPlayerAge(birthDate) {
  if (!birthDate) {
    return null
  }
  const age = new Date().getFullYear() - new Date(birthDate).getFullYear()
  return age || null
}

/**
 * Retrieves the player data from the Redux store.
 *
 * @returns {{
 *   birthDate: string|null,
 *   firstName: string,
 *   lastName: string,
 *   playerId: string,
 *   affiliateId: string|null
 * }}
 */
function usePlayerData() {
  const birthDate = ReactRedux.useSelector(state =>
    Player.getBirthdate(state.player)
  )
  const firstName =
    ReactRedux.useSelector(state => Player.getFirstName(state.player)) || ''
  const lastName =
    ReactRedux.useSelector(state => Player.getLastName(state.player)) || ''
  const playerId = ReactRedux.useSelector(state =>
    Player.getUsername(state.player)
  )
  const affiliateId = ReactRedux.useSelector(state =>
    Player.getAffiliateId(state.player)
  )
  const depositCount = ReactRedux.useSelector(state =>
    Player.getDepositCount(state.player)
  )
  const lastDepositCents = ReactRedux.useSelector(state =>
    Player.getLastDepositAmount(state.player)
  )
  const totalDepositCents = ReactRedux.useSelector(state =>
    Player.getTotalDepositsCents(state.player)
  )

  return {
    birthDate,
    firstName,
    lastName,
    playerId,
    affiliateId,
    depositCount,
    lastDepositCents,
    totalDepositCents,
  }
}

export const FluidContext = React.createContext(null)

/**
 * Provides Fluid initialization context and parameters to child components.
 *
 * This provider is responsible for dynamically loading the Fluid widget script, initializing it,
 * and passing configuration parameters derived from the Redux state and authenticated user session.
 *
 * ⚠️ The Fluid script should only be loaded and initialized under the following conditions:
 * - The user must be **authenticated**
 * - The script must **not already be initialized or loaded**
 * - Both `fluidWidgetUrl` and `fluidOperatorId` must be available in the configuration
 * - The code must run on the **client-side only** (`window` must be defined)
 * - The `bonuses` must be fetched before the script is loaded (used as widget data input)
 *
 * Script loading and initialization are idempotent, tracked by `hasInitializedRef` and `hasLoadedRef`
 * to prevent redundant requests.
 *
 * Cleanup logic ensures the script is removed on unmount or parameter change, resetting initialization flags.
 *
 * @param {{
 *   children: React.ReactNode
 *   translate: (key: string) => string,
 *   bonuses: Array|null
 * }} props - The React children to wrap with the provider
 * @returns {JSX.Element} A React context provider for Fluid widget integration
 */
export function FluidProvider({ children, translate, offers, depositOffers }) {
  const dispatch = ReactRedux.useDispatch()

  const hasInitializedRef = React.useRef(false)
  const hasLoadedRef = React.useRef(false)

  const { authenticated, token } = Herz.Auth.useSession()
  const configuration = ServerConfiguration.useContext()
  const {
    birthDate,
    firstName,
    lastName,
    playerId,
    affiliateId,
    depositCount,
    lastDepositCents,
    totalDepositCents,
  } = usePlayerData()
  const playerAge = getPlayerAge(birthDate)

  const fluidOperatorId = ReactRedux.useSelector(state =>
    Configuration.getFluidOperatorId(state.configuration)
  )
  const fluidWidgetUrl = ReactRedux.useSelector(state =>
    Configuration.getFluidWidgetUrl(state.configuration)
  )

  const [status, setStatus] = React.useState('idle')

  const bonuses = React.useMemo(() => {
    if (!offers && !depositOffers) {
      return null
    }
    return [
      ...offers.map(offer => buildBonus(offer, translate)),
      ...depositOffers.map(offer => buildDepositOffer(offer, translate)),
    ]
  }, [offers, depositOffers, translate])

  function cleanFluidScript() {
    const script = document.querySelector(`script[src="${fluidWidgetUrl}"]`)
    if (script) {
      script.remove()
    }

    hasInitializedRef.current = false
    hasLoadedRef.current = false
  }

  React.useEffect(() => {
    if (typeof window === 'undefined') {
      return
    }
    if (!fluidWidgetUrl || !fluidOperatorId) {
      return
    }

    if (!authenticated && (hasInitializedRef.current || hasLoadedRef.current)) {
      // If the user is not authenticated and the script was initialized or loaded, clean it up
      cleanFluidScript()

      if (status !== 'idle') {
        setStatus('idle')
      }

      return
    }

    if (!authenticated) {
      return
    } // Skip loading script if not authenticated
    if (hasInitializedRef.current || hasLoadedRef.current) {
      return
    } // Prevent re-initialization if already initialized or loaded
    if (status !== 'idle') {
      return
    } // Prevent re-initialization if already loading or ready
    if (!bonuses) {
      return
    } // Skip loading script if bonuses are not fetched

    loadFluidScript()

    return () => {
      cleanFluidScript()
    }
  }, [fluidWidgetUrl, authenticated, hasInitializedRef, hasLoadedRef, bonuses])

  const widgetParams = React.useMemo(
    () => ({
      userId: playerId,
      sessionId: token,
      locale: configuration?.locale?.slug,
      countryCode: configuration?.country?.alpha3,
      currencyCode: configuration?.currency?.code || 'EUR',
      userData: {
        age: playerAge,
        fullName: `${firstName} ${lastName}`,
        affiliateId,
        depositCount,
        previousDeposit:
          lastDepositCents != null ? lastDepositCents / 100 : null,
        lifetimeDeposit:
          totalDepositCents != null ? totalDepositCents / 100 : null,
      },
      bonuses,
      widgetUrl: fluidWidgetUrl,
      operatorId: fluidOperatorId,
    }),
    [
      playerId,
      token,
      configuration,
      playerAge,
      firstName,
      lastName,
      affiliateId,
      depositCount,
      lastDepositCents,
      totalDepositCents,
      bonuses,
      fluidWidgetUrl,
      fluidOperatorId,
    ]
  )

  async function initialiseFluid(params, retries = 4, delay = 1000) {
    // eslint-disable-next-line no-console
    console.info('%cFluid script: initialising...', 'color: lightgreen')

    let attempt = 0
    while (attempt < retries) {
      try {
        // eslint-disable-next-line no-undef
        await fluid.init(params)

        setStatus('ready')
        // eslint-disable-next-line no-console
        console.info('%cFluid script: initialised', 'color: lightgreen')
        break
      } catch (err) {
        attempt++
        if (attempt >= retries) {
          // eslint-disable-next-line no-console
          console.error('Error initialising Fluid script', err)
          setStatus('error')
          dispatch(
            Notifications.add({
              message: 'error.fluid-initialisation-failed',
              level: 'error',
            })
          )
        } else {
          // eslint-disable-next-line no-console
          console.warn(
            `Fluid init failed (attempt ${attempt}), retrying in ${delay}ms...`
          )
          await new Promise(res => setTimeout(res, delay))
        }
      } finally {
        if (attempt >= retries) {
          hasInitializedRef.current = true
        }
      }
    }
    if (attempt < retries) {
      hasInitializedRef.current = true
    }
  }

  function loadFluidScript() {
    const existingScript = document.querySelector(
      `script[src="${fluidWidgetUrl}"]`
    )
    if (existingScript) {
      hasInitializedRef.current = true
      setStatus('ready')
      return
    }

    // eslint-disable-next-line no-console
    console.info('%cFluid script: loading...', 'color: lightgreen')
    const script = document.createElement('script')
    script.src = fluidWidgetUrl
    script.async = true
    script.type = 'module'

    setStatus('loading')

    script.onload = async () => {
      // eslint-disable-next-line no-console
      console.info('%cFluid script: loaded', 'color: lightgreen')
      initialiseFluid(widgetParams)
    }

    script.onerror = err => {
      // eslint-disable-next-line no-console
      console.error('Fluid script failed to load.', err)
      setStatus('error')
      dispatch(
        Notifications.add({
          message: 'error.fluid-script-failed',
          level: 'error',
        })
      )
    }

    document.body.appendChild(script)
    hasLoadedRef.current = true
  }

  return (
    <FluidContext.Provider value={{ status, config: widgetParams }}>
      {children}
    </FluidContext.Provider>
  )
}

FluidProvider.propTypes = {
  children: PropTypes.node.isRequired,
  offers: PropTypes.arrayOf(PropTypes.object),
  depositOffers: PropTypes.arrayOf(PropTypes.object),
  translate: PropTypes.func.isRequired,
}

/**
 * @name useFluid
 * @module fluid/use-fluid
 *
 * React hook to access Fluid status and parameters.
 *
 * @description
 * This hook provides access to the Fluid context, which contains the current
 * status of the Fluid widget and its configuration parameters. It throws an
 * error if used outside of a `FluidProvider`, ensuring that the component
 * @returns {{
 *   status: 'idle'|'loading'|'ready'|'error',
 *   config: FluidParams
 * }}
 * @throws {Error} If used outside of a FluidProvider.
 * @example
 * const { status, config } = useFluid()
 */
export function useFluid() {
  const context = React.useContext(FluidContext)
  if (!context) {
    throw new Error('useFluid must be used within a <FluidProvider>')
  }
  return context
}
